import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { App } from 'antd';
import type { UploadFile } from 'antd';
import { useInboundPackageByIdQuery, useUpdatePurchaseOrderStatusMutation } from 'api/warehouse';
import { useAttachmentBulkCreateMutation, useAttachmentDeleteMutation } from 'api/global';
import { useAttachmentFileBulkCreateMutation, useAttachmentFileDeleteMutation } from 'api/files';
import { WAREHOUSE_ROUTES } from '../../../constants/routes';

interface ItemResponse {
  id: string;
  sku: string;
  status: string;
  quantity: number;
  description?: string;
  attachments: any[];
  step?: string | null;
  itemPackagingResponses?: any[]; // Packages data from API
}

interface OrderDetails {
  id: string;
  uuid: string;
  name?: string;
  status: string;
  supplier?: {
    id: string;
    uuid?: string;
    name: string;
    status: string;
    verified?: boolean;
  };
  completedItemsPercentage?: number;
  itemsResponseList?: ItemResponse[];
}

export const useOrderDetails = () => {
  const { message } = App.useApp();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  // Get expandedItemId from location.state (when coming from outbound)
  const expandedItemId = (location.state as any)?.expandedItemId;
  const [order, setOrder] = useState<OrderDetails | null>(null);
  const [uploadingItems, setUploadingItems] = useState<Set<string>>(new Set());
  const [showAllDoneModal, setShowAllDoneModal] = useState(false);

  const { data, isLoading, error } = useInboundPackageByIdQuery(id || '', {
    skip: !id,
  });

  const [createBulkAttachments] = useAttachmentBulkCreateMutation();
  const [uploadBulkAttachmentFiles] = useAttachmentFileBulkCreateMutation();
  const [deleteAttachment] = useAttachmentDeleteMutation();
  const [deleteAttachmentFile] = useAttachmentFileDeleteMutation();
  const [updatePurchaseOrderStatus] = useUpdatePurchaseOrderStatusMutation();

  useEffect(() => {
    if (data) {
      setOrder(data as any);
    }
  }, [data]);

  useEffect(() => {
    if (error) {
      message.error('Failed to load order details');
      navigate(WAREHOUSE_ROUTES.inboundPackages);
    }
  }, [error, navigate, message]);

  // Check if all items are checked and order is not completed
  const allItemsChecked = useMemo(() => {
    if (!order || !order.itemsResponseList || order.itemsResponseList.length === 0) {
      return false;
    }
    return order.itemsResponseList.every((item) => item.status === 'CHECKED') && order.status !== 'COMPLETED';
  }, [order]);

  const handleScanItem = useCallback(() => {
    navigate(WAREHOUSE_ROUTES.captureItem.replace(':id', id || ''));
  }, [id, navigate]);

  const handleShowCompleteModal = useCallback(() => {
    setShowAllDoneModal(true);
  }, []);

  const handleEditItem = useCallback(
    (item: ItemResponse) => {
      const step = item.step;

      // Navigate to the appropriate page based on the step
      if (!step || step === 'STEP_1') {
        // STEP_1: Go to capture page - initial state (scan screen)
        navigate(WAREHOUSE_ROUTES.captureItem.replace(':id', id || ''), {
          state: { itemId: item.id, sku: item.sku, isInitialScan: true },
        });
      } else if (step === 'STEP_2') {
        // STEP_2: Go to capture page - already identified state
        navigate(WAREHOUSE_ROUTES.captureItem.replace(':id', id || ''), {
          state: { itemId: item.id, sku: item.sku, isInitialScan: false },
        });
      } else if (step === 'STEP_3') {
        // STEP_3: Go to item details page
        navigate(WAREHOUSE_ROUTES.itemDetails.replace(':id', id || ''), {
          state: { itemId: item.id, sku: item.sku },
        });
      } else if (step === 'STEP_4') {
        // STEP_4: Go to packaging page - packages will be fetched from API
        navigate(WAREHOUSE_ROUTES.packaging.replace(':id', id || ''), {
          state: {
            itemId: item.id,
            sku: item.sku,
          },
        });
      } else {
        // Default to capture page - initial state
        navigate(WAREHOUSE_ROUTES.captureItem.replace(':id', id || ''), {
          state: { itemId: item.id, sku: item.sku, isInitialScan: true },
        });
      }
    },
    [id, navigate]
  );

  const handleRaiseClaim = useCallback(
    (item: ItemResponse) => {
      navigate(WAREHOUSE_ROUTES.raiseClaim.replace(':id', id || ''), {
        state: { itemId: item.id, sku: item.sku },
      });
    },
    [id, navigate]
  );

  const handleDeleteAttachment = useCallback(
    async (attachmentId: string, fileName: string) => {
      try {
        message.loading({ content: 'Deleting attachment...', key: `delete-${attachmentId}` });

        // Step 1: Delete attachment record
        const { data } = await deleteAttachment({
          entityName: 'PURCHASE_ORDER_ITEM',
          id: attachmentId,
          service: 'wrd',
        });

        if (!data?.success) {
          throw new Error('Failed to delete attachment');
        }

        // Step 2: If not inherited, delete the file from FSS
        if (!data.data?.inherited) {
          const { data: fileData } = await deleteAttachmentFile({
            id: attachmentId,
            name: fileName,
          });

          if (!fileData?.success) {
            throw new Error('Failed to delete file');
          }
        }

        message.success({ content: 'Attachment deleted successfully', key: `delete-${attachmentId}` });
        return true;
      } catch (error: any) {
        message.error({
          content: error?.message || 'Failed to delete attachment',
          key: `delete-${attachmentId}`,
        });
        return false;
      }
    },
    [deleteAttachment, deleteAttachmentFile, message]
  );

  const handleFileUpload = useCallback(
    async (itemId: string, files: UploadFile[]) => {
      if (!files || files.length === 0) {
        message.error('No files selected');
        return { success: false, attachmentIds: [] };
      }

      // Validate all files have originFileObj
      const validFiles = files.filter((file) => file.originFileObj);
      if (validFiles.length === 0) {
        message.error('Invalid files');
        return { success: false, attachmentIds: [] };
      }

      try {
        setUploadingItems((prev) => new Set(prev).add(itemId));
        message.loading({
          content: `Uploading ${validFiles.length} image(s)...`,
          key: `upload-${itemId}`,
        });

        // Step 1: Create attachment records using bulk endpoint (one for each file)
        const attachments = validFiles.map(() => ({
          entityId: itemId,
          entityName: 'PURCHASE_ORDER_ITEM',
          attachmentType: 'WAREHOUSE',
        }));

        const attachmentResponse: any = await createBulkAttachments({
          attachments,
          service: 'wrd',
        });

        if (!attachmentResponse?.data?.success) {
          throw new Error('Failed to create attachment records');
        }

        const attachmentIds = attachmentResponse.data.data;
        if (!Array.isArray(attachmentIds) || attachmentIds.length === 0) {
          throw new Error('No attachment IDs returned');
        }

        // Step 2: Upload all files using bulk endpoint
        const formData = new FormData();
        formData.append('attachmentIds', attachmentIds.join(','));
        validFiles.forEach((file) => {
          formData.append('files', file.originFileObj as File);
        });
        formData.append('serviceName', 'WRD');

        const fileResponse: any = await uploadBulkAttachmentFiles({
          data: formData,
          entityName: 'PURCHASE_ORDER_ITEM',
        });

        if (!fileResponse?.data?.success) {
          throw new Error('Failed to upload files');
        }

        message.success({
          content: `${validFiles.length} image(s) uploaded successfully`,
          key: `upload-${itemId}`,
        });
        return { success: true, attachmentIds };
      } catch (error: any) {
        message.error({
          content: error?.message || 'Failed to upload images',
          key: `upload-${itemId}`,
        });
        return { success: false, attachmentIds: [] };
      } finally {
        setUploadingItems((prev) => {
          const next = new Set(prev);
          next.delete(itemId);
          return next;
        });
      }
    },
    [createBulkAttachments, uploadBulkAttachmentFiles, message]
  );

  const handleCompleteOrder = useCallback(async () => {
    if (!id) return;

    try {
      message.loading({ content: 'Completing order...', key: 'complete-order' });

      const response: any = await updatePurchaseOrderStatus({
        id,
        status: 'COMPLETED',
      });

      // Check for error response from RTK Query
      if (response.error) {
        const apiError = response.error;
        if (apiError.data?.data?.error_message && Array.isArray(apiError.data.data.error_message)) {
          throw new Error(apiError.data.data.error_message[0]);
        }
        throw new Error('Failed to complete order');
      }

      if (!response?.data?.success) {
        throw new Error('Failed to complete order');
      }

      message.success({ content: 'Order completed successfully', key: 'complete-order' });
      setShowAllDoneModal(false);

      // Navigate back to inbound packages list
      navigate(WAREHOUSE_ROUTES.inboundPackages);
    } catch (error: any) {
      message.error({
        content: error?.message || 'Failed to complete order',
        key: 'complete-order',
      });
    }
  }, [id, updatePurchaseOrderStatus, navigate, message]);

  const handleBackToItems = useCallback(() => {
    setShowAllDoneModal(false);
  }, []);

  return {
    order,
    isLoading,
    handleScanItem,
    handleFileUpload,
    handleDeleteAttachment,
    handleEditItem,
    handleRaiseClaim,
    uploadingItems,
    expandedItemId, // For auto-expanding item when coming from outbound
    showAllDoneModal,
    handleCompleteOrder,
    handleBackToItems,
    allItemsChecked, // To show Complete button instead of Scan Item
    handleShowCompleteModal,
  };
};
